import type { SandboxMemorySnapshot } from '@wegic/site-sdk/shared';
import { useChatStore } from '@/stores/chat';
import { usePreviewStore } from '@/stores/preview';
import {
  GROWTH_ANOMALY_THRESHOLD_MB_PER_MIN,
  GROWTH_TICK_MS,
  IDLE_SETTLE_DELAY_MS,
  MEMORY_SAMPLE_ANCHOR,
  MIN_SAMPLE_INTERVAL_MS,
  type SandboxMemoryPullStatus,
} from './const';
import { computeGrowthMbPerMin, type HeapReading, readJsHeap, reportMemorySample } from './helper';

/**
 * sandbox 内存拉取器。由持有 sandbox iframe ref 的组件（PreviewPanel）注册，
 * 内部通过 siteManager.callMethod(iframe, GET_MEMORY) 拉取 sandbox 进程内存。
 * 返回 null 表示当前拉不到（无 iframe / 非 sandbox 源 / RPC 失败），优雅降级。
 */
export interface SandboxMemoryPullResult {
  snapshot: SandboxMemorySnapshot | null;
  status: SandboxMemoryPullStatus;
  durationMs: number;
  error?: string;
}

export type SandboxMemoryProvider = () => Promise<SandboxMemoryPullResult>;

/**
 * 主 frame 内存监控。
 *
 * 采样策略（事件锚定，不轮询）：
 *   baseline   — 首次 sandbox live 后打一次，作为 session 分母
 *   chat_end   — isStreaming true→false，代表本轮峰值
 *   idle_settle — chat_end 后 15s，代表回落值；idle_settle − baseline ≈ 常驻增量
 *   unload     — 页面隐藏/卸载，beacon 兜底
 *
 * 客户端只报原始数，delta/per-round-growth 等派生量由数仓计算。
 */
class MemoryMonitor {
  private initialized = false;
  private baselineDone = false;
  private lastSampleAt = 0;
  private idleSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeStreaming: (() => void) | null = null;
  private unsubscribeRuntimeStatus: (() => void) | null = null;
  private unloadHandler: (() => void) | null = null;
  // ── 空闲增长异常检测 ──
  private growthTimer: ReturnType<typeof setInterval> | null = null;
  private lastGrowthReading: HeapReading | null = null;
  // ── session 级轮次计数：从 0 开始，每完成一轮流式 +1，与 chat 历史消息解耦 ──
  // baseline 天然为 0；chat_end/idle_settle 反映「本次会话第几轮」，避免刷新已有对话导致 round_index 虚高。
  private sessionRoundCount = 0;
  // ── sandbox 内存拉取器（Phase 2，由 PreviewPanel 注册）──
  private sandboxMemoryProvider: SandboxMemoryProvider | null = null;

  /** 注册 sandbox 内存拉取器。持有 iframe ref 的组件挂载时调用。 */
  registerSandboxMemoryProvider(provider: SandboxMemoryProvider): void {
    this.sandboxMemoryProvider = provider;
  }

  /** 注销拉取器。组件卸载时调用，避免持有失效的 iframe 引用。 */
  unregisterSandboxMemoryProvider(provider: SandboxMemoryProvider): void {
    if (this.sandboxMemoryProvider === provider) {
      this.sandboxMemoryProvider = null;
    }
  }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.subscribeStreaming();
    this.subscribeSandboxRuntime();
    this.listenUnload();
    this.startGrowthDetection();
  }

  destroy(): void {
    this.unsubscribeStreaming?.();
    this.unsubscribeRuntimeStatus?.();
    if (this.unloadHandler) {
      document.removeEventListener('visibilitychange', this.unloadHandler);
      this.unloadHandler = null;
    }
    this.unsubscribeStreaming = null;
    this.unsubscribeRuntimeStatus = null;
    this.cancelIdleSettle();
    this.stopGrowthDetection();
    this.initialized = false;
    this.baselineDone = false;
    this.sessionRoundCount = 0;
  }

  // ── baseline ────────────────────────────────────────────────────────────

  /**
   * 订阅 sandboxRuntimeStatus，首次进入 live 状态时打 baseline。
   * 每个 session 只打一次——projectId 切换不重置，
   * 因为 session 级的分母用第一次稳定态即可。
   */
  private subscribeSandboxRuntime(): void {
    let previousRuntimeStatus = usePreviewStore.getState().sandboxRuntimeStatus;

    this.unsubscribeRuntimeStatus = usePreviewStore.subscribe((state) => {
      const runtimeStatus = state.sandboxRuntimeStatus;

      if (!this.baselineDone && previousRuntimeStatus !== 'live' && runtimeStatus === 'live') {
        this.baselineDone = true;
        void this.sample(MEMORY_SAMPLE_ANCHOR.Baseline);
      }

      previousRuntimeStatus = runtimeStatus;
    });
  }

  // ── chat_end + idle_settle ───────────────────────────────────────────────

  /**
   * 订阅 isStreaming 变化：
   *   true  → 取消未触发的 idle_settle（连续对话中，上一轮的回落已无意义）
   *   false → 报 chat_end，并在 IDLE_SETTLE_DELAY_MS 后报 idle_settle
   */
  private subscribeStreaming(): void {
    let prevStreaming = useChatStore.getState().isStreaming;

    this.unsubscribeStreaming = useChatStore.subscribe((state) => {
      const isStreaming = state.isStreaming;

      if (isStreaming === prevStreaming) {
        return;
      }

      prevStreaming = isStreaming;

      if (isStreaming) {
        // 用户发起新一轮对话，取消上一轮的 idle_settle
        this.cancelIdleSettle();
        return;
      }

      // 流式结束：本轮完成，session 轮次 +1（先递增再采样，chat_end 即带上新轮次号）
      this.sessionRoundCount += 1;
      void this.sample(MEMORY_SAMPLE_ANCHOR.ChatEnd);
      this.scheduleIdleSettle();
    });
  }

  private scheduleIdleSettle(): void {
    this.cancelIdleSettle();
    this.idleSettleTimer = setTimeout(() => {
      this.idleSettleTimer = null;
      void this.sample(MEMORY_SAMPLE_ANCHOR.IdleSettle);
    }, IDLE_SETTLE_DELAY_MS);
  }

  private cancelIdleSettle(): void {
    if (this.idleSettleTimer !== null) {
      clearTimeout(this.idleSettleTimer);
      this.idleSettleTimer = null;
    }
  }

  // ── unload ──────────────────────────────────────────────────────────────

  /**
   * 页面隐藏时用 beacon 兜底上报。
   * 兜底价值：能捕获用户直接关 tab 的内存终态，补全 idle_settle 可能漏掉的 session。
   *
   * 注意：这里只做 sample,不再单独 flush——RuntimeDiagnostics 的 visibility_hidden
   * 会在后续触发统一 flush(二者都在同一 visibilitychange 内),避免重复调用 beacon。
   */
  private listenUnload(): void {
    this.unloadHandler = () => {
      if (document.visibilityState === 'hidden') {
        // pullSandbox=false：页面隐藏时不能等 RPC，同步上报后由 RuntimeDiagnostics 统一 flush
        void this.sample(MEMORY_SAMPLE_ANCHOR.Unload, undefined, { pullSandbox: false });
      }
    };
    document.addEventListener('visibilitychange', this.unloadHandler);
  }

  // ── core sample ─────────────────────────────────────────────────────────

  /**
   * 采样并上报。
   *
   * 频率闸同步生效（先占住 lastSampleAt），再按需异步拉取 sandbox 内存。
   * 仅当 targetLayer === 'live' 且已注册 provider 时才拉——
   * 非 sandbox 源时 iframe 是 pre-release/blank，RPC 只会白白超时。
   *
   * pullSandbox=false 用于 unload：页面隐藏时 RPC 无法 resolve，
   * 此时跳过 sandbox 拉取，保证 reportMemorySample 在同一 tick 内同步完成，
   * 好让紧随其后的 flush(beacon) 能带上这条样本。
   */
  private async sample(
    anchor: typeof MEMORY_SAMPLE_ANCHOR[keyof typeof MEMORY_SAMPLE_ANCHOR],
    extra?: Record<string, unknown>,
    options?: { pullSandbox?: boolean },
  ): Promise<void> {
    const now = Date.now();
    if (now - this.lastSampleAt < MIN_SAMPLE_INTERVAL_MS) {
      return;
    }
    this.lastSampleAt = now;

    const pullSandbox = options?.pullSandbox ?? true;
    const hasProvider = this.sandboxMemoryProvider !== null;
    const targetsLive = usePreviewStore.getState().targetLayer === 'live';
    const canPull = pullSandbox && hasProvider && targetsLive;

    if (!canPull) {
      reportMemorySample(anchor, extra, null, this.sessionRoundCount, {
        status: !pullSandbox
          ? 'pull_disabled'
          : !hasProvider
            ? 'provider_missing'
            : 'target_not_live',
        durationMs: 0,
      });
      return;
    }

    let result: SandboxMemoryPullResult = {
      snapshot: null,
      status: 'rpc_error',
      durationMs: 0,
    };
    try {
      result = await this.sandboxMemoryProvider!();
    } catch {
      // RPC 失败（超时 / iframe 已卸载）时优雅降级，仍上报主 frame 数据
      result = {
        snapshot: null,
        status: 'rpc_error',
        durationMs: 0,
      };
    }
    reportMemorySample(anchor, extra, result.snapshot, this.sessionRoundCount, {
      status: result.status,
      durationMs: result.durationMs,
      error: result.error,
    });
  }

  // ── 空闲增长异常检测 ──────────────────────────────────────────────────────

  /**
   * 低频 tick 静默读内存，仅当相邻两次涨幅超阈值才上报一条 growth_anomaly。
   * 门控：只在 document 可见且 sandbox live 时才比对，其余情况丢弃基准读数，
   * 避免后台标签页 / sandbox 未就绪时的伪增长噪音。
   */
  private startGrowthDetection(): void {
    this.growthTimer = setInterval(() => {
      this.growthTick();
    }, GROWTH_TICK_MS);
  }

  private stopGrowthDetection(): void {
    if (this.growthTimer !== null) {
      clearInterval(this.growthTimer);
      this.growthTimer = null;
    }
    this.lastGrowthReading = null;
  }

  private growthTick(): void {
    // 门控：不可见或 sandbox 未 live 时，清空基准（避免跨越隐藏期算出伪增长），下轮重新建立
    const isVisible = document.visibilityState === 'visible';
    const isLive = usePreviewStore.getState().sandboxRuntimeStatus === 'live';
    if (!isVisible || !isLive) {
      this.lastGrowthReading = null;
      return;
    }

    const heap = readJsHeap();
    if (!heap) {
      return; // 非 Chromium，无 heap 数据
    }

    const curr: HeapReading = { usedJsHeap: heap.usedJsHeap, at: Date.now() };
    const growthMbPerMin = computeGrowthMbPerMin(this.lastGrowthReading, curr);
    this.lastGrowthReading = curr;

    if (growthMbPerMin !== null && growthMbPerMin > GROWTH_ANOMALY_THRESHOLD_MB_PER_MIN) {
      void this.sample(MEMORY_SAMPLE_ANCHOR.GrowthAnomaly, {
        growth_mb_per_min: Math.round(growthMbPerMin),
        growth_threshold_mb_per_min: GROWTH_ANOMALY_THRESHOLD_MB_PER_MIN,
      });
    }
  }
}

export const memoryMonitor = new MemoryMonitor();

/** 在 AppLifecycle.afterAppMount 中调用。 */
export function initMemoryMonitor(): void {
  memoryMonitor.init();
}
